import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, TextField, Divider, Typography, Box, Chip
} from '@mui/material';
import { useMembers } from '../../contexts/MemberContext';
import { ServiceType } from '../../interfaces/constants';

type ServiceLike = {
  name?: string;
  type?: ServiceType | string;
  price?: number;
  salary?: number;
  groupSize?: number;
  rate?: number;
  note?: string;
  serviceId?: string;
  studentMemberIds?: string[];
  teacherId?: string | { memberId?: string; [k: string]: any };
  [k: string]: any;
};

type Props = {
  open: boolean;
  onClose: () => void;
  service?: ServiceLike | null;
};

const ViewServiceDialog: React.FC<Props> = ({ open, onClose, service }) => {
  const { members } = useMembers();
  const findMember = (id?: string) => members.find((m) => m.memberId === id);

  // teacher
  const teacherMemberId =
    typeof service?.teacherId === 'string'
      ? (service?.teacherId as string)
      : service?.teacherId?.memberId;

  const teacher = findMember(teacherMemberId);
  const teacherName = teacher ? `${teacher.firstName ?? ''} ${teacher.lastName ?? ''}`.trim() : '';
  const teacherEmail = teacher?.email ?? teacherMemberId;

  // students
  const studentIds = service?.studentMemberIds ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600, fontSize: 22 }}>View Service</DialogTitle>

      <DialogContent dividers>
        {/* Section: Course Info */}
        <Divider sx={{ my: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Course Info</Typography>
        </Divider>
        <Grid container spacing={6}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={service?.name ?? ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Type"
              value={service?.type ?? ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Price"
              value={service?.price != null ? `$${service.price.toFixed(2)}` : ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Salary"
              value={service?.salary != null ? `$${service.salary.toFixed(2)}` : ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Group Size"
              value={service?.groupSize ?? ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Rate"
              value={service?.rate != null ? `${service.rate}/hr` : ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
        </Grid>

        {/* Section: Teacher */}
        <Divider sx={{ my: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Teacher</Typography>
        </Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={teacherName}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email / ID"
              value={teacherEmail ?? ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="standard"
            />
          </Grid>
        </Grid>

        {/* Section: Students */}
        <Divider sx={{ my: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Students</Typography>
        </Divider>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {studentIds.length ? (
            studentIds.map((id) => {
              const m = findMember(id);
              const label = m ? `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email : id;
              return <Chip key={id} label={label} size="small" />;
            })
          ) : (
            <Typography color="text.disabled">—</Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewServiceDialog;
